import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as _ from 'lodash';
import dayjs from 'dayjs';
import QRCode from 'qrcode.react';
import html2canvas from 'html2canvas';
import { Button, Checkbox, Drawer, Form, Image, Input, Modal, Select, Skeleton, Space, Tooltip, TreeSelect, Timeline, Upload, message } from 'antd';
import { getAllAgendasApi } from '@services/Agenda';
import './index.less';
import config from '@config';
import { prepareBCOSSUrl, getCityNameByCode, getProvinceNameByCode } from '@utils/utils';
import { filterTreeNodeByTitle } from '@/utils/antdTreeSelect';
import WorkExperiences from '@/components/common/WorkExperiences';
import mpCode from '../../../../../static/images/mp_code.png';

const PreviewAgendasDrawer = props => {
    const params = useParams();
    let conferenceId = params?.id || 0; // 会议项目 ID
    conferenceId = parseInt(conferenceId, 10) || 0;
    // console.log('conferenceId', conferenceId);

    const { visiblePreviewAgendasDrawer, setVisiblePreviewAgendasDrawer, conference } = props;

    const [agendaData, setAgendaData] = useState(null); // 日程数据
    const [currentAgendaDate, setCurrentAgendaDate] = useState(''); // 当前日程日期
    const [currentConferenceHallId, setCurrentConferenceHallId] = useState(); // 当前场次 ID
    const [visibleAgendaOnly, setVisibleAgendaOnly] = useState(true); // 仅看显示日程
    const [conferenceHallTreeData, setConferenceHallTreeData] = useState([]); // 会议场次树形结构数据

    // H5模板设置相关状态
    const [visibleH5TemplateModal, setVisibleH5TemplateModal] = useState(false);
    const [h5TemplateForm] = Form.useForm();
    const [headerImgList, setHeaderImgList] = useState([]);
    const [qrcodeImg, setQrcodeImg] = useState([]);
    const [localAgendaSettings, setLocalAgendaSettings] = useState(null);

    const defaultAgendaBackgroundColor = '#1677FF';
    const defaultAgendaConfHallTitleColor = '#FFFFFF';
    const defaultAgendaAgendaTaskColor = '#1677FF';

    // 颜色列表
    const colorList = ['#f5222d', '#fa541c', '#fa8c16', '#52c41a', '#1677ff', '#0327ff', '#722ed1', '#eb2f96', '#333333', '#888888', '#ffffff'].map(c => c.toUpperCase());

    const onChangeVisibleAgendaOnlyCheckbox = e => setVisibleAgendaOnly(e.target.checked);

    // H5模板设置相关函数
    const openH5TemplateModal = () => {
        console.log('打开H5版日程样式设置Modal');
        setVisibleH5TemplateModal(true);

        // 优先使用本地状态，如果没有则使用conference中的设置
        let agendaSettings = localAgendaSettings;
        if (!agendaSettings && conference?.agendaSettings) {
            agendaSettings = JSON.parse(conference.agendaSettings);
        }

        // 初始化表单数据
        if (agendaSettings) {
            h5TemplateForm.setFieldsValue({
                web: agendaSettings?.web || {},
            });
            if (agendaSettings?.web?.head_img_url) {
                setHeaderImgList([agendaSettings.web.head_img_url]);
            }
            if (agendaSettings?.web?.qrcode_img_url) {
                setQrcodeImg([agendaSettings.web.qrcode_img_url]);
            }
        }
    };

    // 关闭H5版日程样式设置Modal
    const closeH5TemplateModal = () => {
        setVisibleH5TemplateModal(false);
        h5TemplateForm.resetFields();
        setHeaderImgList([]);
        setQrcodeImg([]);
    };

    // 背景色设置
    const changeBgColor = (item, fieldName) => {
        h5TemplateForm.setFieldValue([...fieldName], item);
    };

    // 图片上传校验
    const checkout = (file, size) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
        if (!isJpgOrPng) {
            message.error('仅支持上传JPG/PNG格式的图片。');
            return false;
        }
        const isLt5M = file.size / 1024 / 1024 < size;
        if (!isLt5M) {
            message.error(`图片不能大于${size}MB。`);
            return false;
        }
        return isJpgOrPng && isLt5M;
    };

    // 获取上传文件URL
    const getFileUrl = url => {
        if (url && url.includes('https')) {
            return url;
        }
        if (url && !url.includes('https')) {
            return config.OSS_PREFIX + url;
        }
        return null;
    };

    // 头图上传
    const imageUpload = async info => {
        const formData = new FormData();
        formData.append('file', info.file);
        formData.append('type', 14);

        try {
            const { fileApi } = await import('@services/Conference');
            const res = await fileApi(formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res?.status_code === 200) {
                const imageUrl = getFileUrl(res.data);
                setHeaderImgList([imageUrl]);
                h5TemplateForm.setFieldValue(['web', 'head_img_url'], imageUrl);
                h5TemplateForm.setFields([{ name: ['web', 'head_img_url'], errors: null }]);
            } else {
                message.error('上传文件失败！');
            }
        } catch (error) {
            message.error('上传文件失败！');
        }
    };

    // 二维码上传
    const qrcodeUpload = async info => {
        const formData = new FormData();
        formData.append('file', info.file);
        formData.append('type', 14);

        try {
            const { fileApi } = await import('@services/Conference');
            const res = await fileApi(formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res?.status_code === 200) {
                const imageUrl = prepareBCOSSUrl(res.data);
                setQrcodeImg([imageUrl]);
                h5TemplateForm.setFieldValue(['web', 'qrcode_img_url'], imageUrl);
            } else {
                message.error('上传文件失败！');
            }
        } catch (error) {
            message.error('上传文件失败！');
        }
    };

    // 移除头图
    const removeHeaderImg = () => {
        setHeaderImgList([]);
        h5TemplateForm.setFieldValue(['web', 'head_img_url'], undefined);
        h5TemplateForm.setFields([{ name: ['web', 'head_img_url'], errors: null }]);
    };

    // 移除二维码图片
    const removeQrcodeImg = () => {
        setQrcodeImg([]);
        h5TemplateForm.setFieldValue(['web', 'qrcode_img_url'], undefined);
        h5TemplateForm.setFields([{ name: ['web', 'qrcode_img_url'], errors: null }]);
    };

    // 保存H5版日程样式设置
    const saveH5TemplateSettings = async () => {
        try {
            const values = await h5TemplateForm.validateFields();
            const currentSettings = conference?.agendaSettings ? JSON.parse(conference.agendaSettings) : {};
            const newSettings = {
                ...currentSettings,
                web: values.web,
            };

            const { updateAgendaSettings } = await import('@services/Agenda');
            const res = await updateAgendaSettings({
                id: conferenceId,
                agendaSettings: JSON.stringify(newSettings),
            });

            if (res.status_code === 200) {
                message.success('H5版日程样式设置保存成功');

                // 更新本地状态，立即反映到界面上
                setLocalAgendaSettings(newSettings);

                closeH5TemplateModal();

                // 这里可以触发父组件刷新会议信息
                if (props.getConferenceInfo) {
                    props.getConferenceInfo(conferenceId);
                }
            } else {
                message.error('保存失败');
            }
        } catch (error) {
            message.error('保存失败');
        }
    };

    // 准备日程列表嘉宾信息
    const prepareAgendaListGuestInfo = data => {
        // console.log('agendaData', agendaData);
        let agendas = null;

        if (!_.isEmpty(data) && !_.isEmpty(data.conferenceList)) {
            agendas = data.conferenceList;

            // 遍历会议日程数据中的 key，即会议日期
            Object.keys(agendas).forEach(confDate => {
                const date = dayjs(confDate);
                if (!date.isValid()) {
                    return;
                }

                // 改日期 key
                const d = date.format('YYYY年M月D日');
                const weekDay = date.day(); // 0=周日, 1=周一...
                const weekText = ['日', '一', '二', '三', '四', '五', '六'][weekDay];
                const day = `${d}（周${weekText}）`;
                // console.log(day)
                if (agendas[confDate] && agendas[confDate].length) {
                    agendas[day] = agendas[confDate];
                }
                delete agendas[confDate];

                // 会议日期-会议场次
                const confHalls = agendas[day];
                // console.log(confHalls);
                confHalls
                    && confHalls.length
                    && confHalls.forEach(confHall => {
                        if (confHall.subHallList && confHall.subHallList.length) {
                            // 有二级会场
                            // 遍历二级会场
                            confHall.subHallList.forEach(subHall => {
                                const hosts = [];
                                const speakers = [];
                                let totalStartTime = '';
                                let totalEndTime = '';

                                const agendaList = [];
                                subHall.agendas
                                    && subHall.agendas.length
                                    && subHall.agendas.forEach((a, idx, arr) => {
                                        if (a.type !== 5 || a.type !== 6) {
                                            // 1主持, 2演讲, 3讨论, 4仪式, 5赞助企业, 6工作人员
                                            const tempAgenda = { ...a };

                                            let startTime = tempAgenda.startTime || '';
                                            startTime = startTime.slice(0, 5);
                                            tempAgenda['startTime'] = startTime;

                                            let endTime = tempAgenda.endTime || '';
                                            endTime = endTime.slice(0, 5);
                                            tempAgenda['endTime'] = endTime;

                                            if (totalStartTime === '') {
                                                totalStartTime = startTime;
                                            } else {
                                                if (totalStartTime > startTime) {
                                                    totalStartTime = startTime;
                                                }
                                            }
                                            if (totalEndTime === '') {
                                                totalEndTime = endTime;
                                            } else {
                                                if (totalEndTime < endTime) {
                                                    totalEndTime = endTime;
                                                }
                                            }

                                            // 日程嘉宾列表
                                            if (tempAgenda.guestExtra && tempAgenda.guestExtra.length) {
                                                const guests = [];

                                                const experts = data.guestList || []; // 备用专家信息列表
                                                tempAgenda.guestExtra
                                                    .filter(g => g.taskName !== '赞助企业' && g.taskName !== '工作人员' && g.invitationAccepted === 1)
                                                    .forEach(guest => {
                                                        experts.forEach(expert => {
                                                            if (guest.expertId === expert.id) {
                                                                guests.push({ ...guest, ...expert });

                                                                const username = expert.memUsername;
                                                                if (tempAgenda.type === 1) {
                                                                    if (hosts.indexOf(username) === -1) {
                                                                        hosts.push(username);
                                                                    }
                                                                } else {
                                                                    if (speakers.indexOf(username) === -1) {
                                                                        speakers.push(username);
                                                                    }
                                                                }
                                                            }
                                                        });
                                                    });

                                                tempAgenda.guestList = guests; // 查找到嘉宾信息回填到日程嘉宾中
                                            }

                                            agendaList.push(tempAgenda);
                                        }
                                    });

                                subHall.agendas = agendaList;

                                subHall['totalStartTime'] = totalStartTime;
                                subHall['totalEndTime'] = totalEndTime;

                                // console.log('totalStartTime', totalStartTime)
                                // console.log('totalEndTime', totalEndTime);

                                if (hosts.length) {
                                    subHall['hosts'] = hosts.join('、');
                                }
                                if (speakers.length) {
                                    subHall['speakers'] = speakers.length > 60 ? speakers.slice(0, 59).join('、') + '等' + speakers.length + '位嘉宾' : speakers.join('、');
                                }
                            });
                        } else {
                            // 无二级会场
                            // 会议场次下的日程列表
                            const hosts = [];
                            const speakers = [];
                            let totalStartTime = '';
                            let totalEndTime = '';

                            const agendaList = [];
                            const confHallAgendas = confHall.agendas;
                            confHallAgendas
                                && confHallAgendas.length
                                && confHallAgendas.forEach((a, idx, arr) => {
                                    if (a.type !== 5 && a.type !== 6) {
                                        // 1主持, 2演讲, 3讨论, 4仪式, 5赞助企业, 6工作人员
                                        const tempAgenda = { ...a };

                                        let startTime = tempAgenda.startTime || '';
                                        startTime = startTime.slice(0, 5);
                                        tempAgenda['startTime'] = startTime.slice(0, 5);

                                        let endTime = tempAgenda.endTime || '';
                                        endTime = endTime.slice(0, 5);
                                        tempAgenda['endTime'] = endTime;

                                        if (totalStartTime === '') {
                                            totalStartTime = startTime;
                                        } else {
                                            if (totalStartTime > startTime) {
                                                totalStartTime = startTime;
                                            }
                                        }
                                        if (totalEndTime === '') {
                                            totalEndTime = endTime;
                                        } else {
                                            if (totalEndTime < endTime) {
                                                totalEndTime = endTime;
                                            }
                                        }

                                        // 日程嘉宾列表
                                        if (tempAgenda.guestExtra && tempAgenda.guestExtra.length) {
                                            const guests = [];
                                            // console.log('guestExtra', a.guestExtra);

                                            const experts = data.guestList || []; // 备用专家信息列表
                                            tempAgenda.guestExtra
                                                .filter(g => g.taskName !== '赞助企业' && g.taskName !== '工作人员' && g.invitationAccepted === 1)
                                                .forEach(guest => {
                                                    experts.forEach(expert => {
                                                        if (guest.expertId === expert.id) {
                                                            guests.push({ ...guest, ...expert });

                                                            const username = expert.memUsername;
                                                            if (tempAgenda.type === 1) {
                                                                if (hosts.indexOf(username) === -1) {
                                                                    hosts.push(username);
                                                                }
                                                            } else {
                                                                if (speakers.indexOf(username) === -1) {
                                                                    speakers.push(username);
                                                                }
                                                            }
                                                        }
                                                    });
                                                });

                                            tempAgenda.guestList = guests; // 查找到嘉宾信息回填到日程嘉宾中
                                        }

                                        agendaList.push(tempAgenda);
                                    }
                                });

                            confHall.agendas = agendaList;

                            confHall['totalStartTime'] = totalStartTime;
                            confHall['totalEndTime'] = totalEndTime;

                            // console.log('totalStartTime', totalStartTime);
                            // console.log('totalEndTime', totalEndTime);

                            if (hosts.length) {
                                confHall['hosts'] = hosts.join('、');
                            }
                            if (speakers.length) {
                                confHall['speakers'] = speakers.length > 60 ? speakers.slice(0, 59).join('、') + '等' + speakers.length + '位嘉宾' : speakers.join('、');
                            }
                        }
                    });
            });
        }

        console.log('agendas', agendas);
        return agendas;
    };

    const [agendaDataLoading, setAgendaDataLoading] = useState(false);

    // 获取日程列表
    const getAgendas = async conferenceId => {
        try {
            setAgendaDataLoading(true);

            const res = await getAllAgendasApi(conferenceId);
            if (res.status_code === 200) {
                const data = res.data;
                // console.log(prepareAgendaListGuestInfo(data));
                setAgendaData(prepareAgendaListGuestInfo(data));
            }

            setAgendaDataLoading(false);
        } catch (e) {
            setAgendaDataLoading(false);
        }
    };

    useEffect(() => {
        if (visiblePreviewAgendasDrawer && conferenceId) {
            getAgendas(conferenceId);
        }
    }, [visiblePreviewAgendasDrawer]);

    // 关闭预览H5版日程Drawer
    const closePreviewAgendasDrawer = () => {
        setCurrentAgendaDate(undefined);
        setCurrentConferenceHallId(undefined);

        setVisiblePreviewAgendasDrawer(false);
    };

    // 检测是否为Safari浏览器
    const isSafari = () => {
        const userAgent = navigator.userAgent.toLowerCase();
        return userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('firefox');
    };

    // 等待图片加载完成
    const waitForImagesToLoad = element => {
        const images = element.querySelectorAll('img');
        const promises = Array.from(images).map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise((resolve, reject) => {
                img.addEventListener('load', resolve);
                img.addEventListener('error', reject);
                // 设置超时防止卡死
                setTimeout(resolve, 3000);
            });
        });
        return Promise.all(promises);
    };

    // 为Safari优化CSS样式
    const optimizeElementForSafari = element => {
        if (!isSafari()) return;

        const styleSheet = document.createElement('style');
        styleSheet.textContent = `
            .agenda-preview-content * {
                -webkit-font-smoothing: antialiased !important;
                -moz-osx-font-smoothing: grayscale !important;
                text-rendering: optimizeLegibility !important;
                font-feature-settings: "liga" 0 !important;
                font-family: SF Pro Text, SF Pro Icons, Helvetica Neue, Helvetica, Arial, sans-serif !important;
            }
            .agenda-preview-content {
                -webkit-transform: translateZ(0) !important;
                transform: translateZ(0) !important;
            }
            .agenda-preview-content table {
                border-collapse: separate !important;
                border-spacing: 0 !important;
            }
            .agenda-preview-content .timeline-wrap {
                position: relative !important;
            }
        `;
        document.head.appendChild(styleSheet);

        return () => {
            document.head.removeChild(styleSheet);
        };
    };

    // 导出H5版日程图片
    const exportHtmlDomAsImage = async (el, imageFileName) => {
        try {
            console.log('开始导出图片，浏览器类型：', isSafari() ? 'Safari' : '其他浏览器');

            // 应用Safari优化
            const cleanupSafariStyles = optimizeElementForSafari(el);

            // 等待所有图片加载完成
            await waitForImagesToLoad(el);

            // 为Safari添加额外的等待时间，确保样式应用完成
            if (isSafari()) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            // Safari优化的html2canvas配置
            const html2canvasOptions = {
                useCORS: true,
                allowTaint: false,
                scale: isSafari() ? 2 : 1, // Safari使用更高的缩放比例提升质量
                logging: false,
                letterRendering: true,
                backgroundColor: '#ffffff',
                width: el.scrollWidth,
                height: el.scrollHeight,
                scrollX: 0,
                scrollY: 0,
                // Safari特殊配置
                ...(isSafari() && {
                    foreignObjectRendering: false, // Safari禁用foreignObject
                    imageTimeout: 5000, // 增加图片超时时间
                    onclone: clonedDoc => {
                        // 为克隆文档添加Safari优化样式
                        const clonedStyle = clonedDoc.createElement('style');
                        clonedStyle.textContent = `
                            * {
                                -webkit-font-smoothing: antialiased !important;
                                -moz-osx-font-smoothing: grayscale !important;
                                font-family: SF Pro Text, SF Pro Icons, Helvetica Neue, Helvetica, Arial, sans-serif !important;
                            }
                            .timeline-wrap .ant-timeline-item-content {
                                position: relative !important;
                                top: 0 !important;
                            }
                        `;
                        clonedDoc.head.appendChild(clonedStyle);
                    },
                }),
            };

            console.log('开始渲染Canvas...');
            const canvas = await html2canvas(el, html2canvasOptions);

            // 清理Safari优化样式
            if (cleanupSafariStyles) {
                cleanupSafariStyles();
            }

            console.log('Canvas渲染完成，开始下载...');

            // Safari的下载处理
            if (isSafari()) {
                // Safari使用Blob方式下载，避免dataURL长度限制
                canvas.toBlob(
                    blob => {
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.style.display = 'none';
                        link.download = imageFileName;
                        link.href = url;

                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        // 清理Blob URL
                        setTimeout(() => {
                            URL.revokeObjectURL(url);
                        }, 100);
                    },
                    'image/png',
                    0.95,
                ); // 使用稍低的质量避免文件过大
            } else {
                // 其他浏览器使用原有方式
                const link = document.createElement('a');
                link.style.display = 'none';
                link.download = imageFileName;
                link.href = canvas.toDataURL('image/png', 1.0);

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                link.remove();
            }

            console.log('图片下载完成');
        } catch (error) {
            console.error('导出图片时出错：', error);
            // 显示用户友好的错误信息
            if (isSafari()) {
                alert('Safari浏览器导出图片失败，建议尝试刷新页面后重试，或使用Chrome浏览器。');
            } else {
                alert('导出图片失败，请重试。');
            }
        }
    };

    // 下载H5版日程图片
    const downloadAgendaImage = () => {
        console.log('开始下载日程图片');
        const el = document.getElementById('agenda-preview-content');
        const fileName = conference.name + '.png';
        exportHtmlDomAsImage(el, fileName);
    };

    // 切换日程日期
    const onChangeAgendaDate = value => {
        console.log('onChangeAgendaDate', value, typeof value);
        setCurrentAgendaDate(value);

        // 切换日程时，清空选择论坛ID
        setCurrentConferenceHallId(undefined);

        const treeData = [];
        const confHalls = agendaData[value];
        confHalls
            && confHalls.length
            && confHalls.forEach(confHall => {
                const node = {
                    title: `${confHall.name} [YHYC#${confHall.id}]`,
                    value: confHall.id,
                    children: [],
                };

                if (confHall.subHallList && confHall.subHallList.length) {
                    confHall.subHallList.forEach(subHall => {
                        node.children.push({
                            title: `${subHall.name} [YHYC#${subHall.id}]`,
                            value: subHall.id,
                        });
                    });
                }

                treeData.push(node);
            });

        // console.log(treeData)
        setConferenceHallTreeData(treeData);
    };

    // 切换会议论坛
    const onChangeConferenceHall = value => {
        console.log('onChangeConferenceHall', value);
        setCurrentConferenceHallId(value); // 选中某个会议论坛

        agendaData
            && Object.keys(agendaData)
            && Object.keys(agendaData)?.length
            && Object.keys(agendaData).forEach(agendaDate => {
                agendaData[agendaDate].forEach(hall => {
                    if (hall.id === value) {
                        // 该会议论坛及其子论坛，都显示
                        hall.previewHidden = 0; // previewHidden 仅用于选择论坛时附加隐藏显示字段

                        hall.subHallList
                            && hall.subHallList.length
                            && hall.subHallList.forEach(subHall => {
                                subHall.previewHidden = 0;
                            });
                    } else {
                        hall.previewHidden = 1;
                        hall.subHallList
                            && hall.subHallList.length
                            && hall.subHallList.forEach(subHall => {
                                if (subHall.id === value) {
                                    hall.previewHidden = 0; // 子论坛要展示，父论坛也不能隐藏
                                    subHall.previewHidden = 0;
                                } else {
                                    subHall.previewHidden = 1;
                                }
                            });
                    }
                });
            });
    };

    // H5版日程样式设置，优先使用本地状态，如果没有则使用conference中的设置
    const agendaSettings = useMemo(() => {
        // 优先使用本地状态，如果没有则使用conference中的设置
        if (localAgendaSettings) {
            return localAgendaSettings;
        }
        if (conference?.agendaSettings) {
            const values = JSON.parse(conference.agendaSettings);
            return values;
        }
        return null;
    }, [localAgendaSettings, conference]);

    return (
        <>
            <Drawer
                closable={{ placement: 'end' }}
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Button size="small" type="link" onClick={() => closePreviewAgendasDrawer()} style={{ paddingLeft: 0 }}>
                            <i className="bi bi-chevron-left anticon"></i>
                            <span style={{ marginLeft: 2 }}>返回</span>
                        </Button>
                        <span>预览H5版日程</span>
                    </div>
                }
                size="100%"
                placement="bottom"
                open={visiblePreviewAgendasDrawer}
                onClose={closePreviewAgendasDrawer}
            >
                <div className="agenda-preview-container">
                    <div className="agenda-preview-title">
                        <div className="conference-name">
                            <Select placeholder="选择日程日期" size="small" style={{ width: 150, marginRight: 8 }} onChange={onChangeAgendaDate} defaultValue={0}>
                                <Select.Option value={0}>全部日期</Select.Option>
                                {agendaData
                                    && Object.keys(agendaData)
                                    && Object.keys(agendaData).map(agendaDate => (
                                        <Select.Option value={agendaDate} key={agendaDate}>
                                            {agendaDate.replace(/（.+?）/, '')}
                                        </Select.Option>
                                    ))}
                            </Select>
                            {!!currentAgendaDate && (
                                <TreeSelect
                                    size="small"
                                    showSearch={{ filterTreeNode: filterTreeNodeByTitle }}
                                    style={{ width: 200, marginRight: 8 }}
                                    styles={{ popup: { root: { minWidth: 500, overflow: 'auto' } } }}
                                    placement="bottomLeft"
                                    allowClear
                                    value={currentConferenceHallId}
                                    onChange={onChangeConferenceHall}
                                    placeholder="选择会议论坛"
                                    treeDefaultExpandAll
                                    treeData={conferenceHallTreeData}
                                />
                            )}
                            <Checkbox checked={visibleAgendaOnly} onChange={onChangeVisibleAgendaOnlyCheckbox}>
                                仅看显示日程
                            </Checkbox>
                        </div>
                        <div className="toolbar">
                            <Space>
                                <Button size="small" onClick={openH5TemplateModal}>
                                    样式设置
                                </Button>
                                <Button type="primary" size="small" onClick={() => downloadAgendaImage()}>
                                    下载日程图片
                                </Button>
                            </Space>
                        </div>
                    </div>

                    <div
                        id="agenda-preview-content"
                        className="agenda-preview-content"
                        style={{
                            backgroundColor: !!agendaSettings?.web?.background_color ? agendaSettings?.web?.background_color : defaultAgendaBackgroundColor,
                            backgroundImage: !!agendaSettings?.web?.head_img_url ? `url("${prepareBCOSSUrl(agendaSettings?.web?.head_img_url)}")` : 'unset',
                        }}
                    >
                        {agendaDataLoading ? (
                            <>
                                <Skeleton />
                                <Skeleton />
                                <Skeleton />
                            </>
                        ) : (
                            <>
                                <h1 style={{ height: !!agendaSettings?.web?.head_img_url ? 424 : 'auto' }}>{!agendaSettings?.web?.head_img_url && <span className="head-title">{conference?.name}</span>}</h1>

                                {agendaData
                                    && Object.keys(agendaData)
                                    && Object.keys(agendaData)
                                        .filter(d => !currentAgendaDate || d === currentAgendaDate)
                                        .map(agendaDate => (
                                            <div className="agenda-date" key={`agendaDate_${agendaDate}`}>
                                                {(!!conference?.provinceCode || !!conference?.cityCode) && (
                                                    <div className="conference-area">
                                                        会议地点：
                                                        {getProvinceNameByCode(conference?.provinceCode) === getCityNameByCode(conference?.provinceCode, conference?.cityCode)
                                                            ? getProvinceNameByCode(conference?.provinceCode)
                                                            : `${getProvinceNameByCode(conference?.provinceCode)} ${getCityNameByCode(conference?.provinceCode, conference?.cityCode)}`}
                                                    </div>
                                                )}

                                                {!!conference?.place && (
                                                    <div className="conference-place">
                                                        <i className="bi bi-geo-alt"></i>
                                                        <span>{conference.place}</span>
                                                    </div>
                                                )}

                                                <h2>{agendaDate}</h2>
                                                {agendaData[agendaDate]
                                                    && agendaData[agendaDate].length
                                                    && agendaData[agendaDate]
                                                        .filter(h => ((visibleAgendaOnly && !h.hidden) || !visibleAgendaOnly) && (!currentConferenceHallId || !h.previewHidden))
                                                        .map(hall => {
                                                            return (
                                                                <div className="conference-hall-box" key={`hall_${hall.id}`}>
                                                                    {hall.subHallList && hall.subHallList.filter(h => ((visibleAgendaOnly && !h.hidden) || !visibleAgendaOnly) && (!currentConferenceHallId || !h.previewHidden)).length ? (
                                                                        hall.subHallList
                                                                            .filter(h => ((visibleAgendaOnly && !h.hidden) || !visibleAgendaOnly) && (!currentConferenceHallId || !h.previewHidden))
                                                                            .map(subHall => (
                                                                                <table key={`hall_${hall.id}_${subHall.id}`}>
                                                                                    <div className="conference-title-wrap">
                                                                                        <div className="conference-hall">
                                                                                            <div className="hall-text" style={{ color: agendaSettings?.web?.conf_hall_title_color || defaultAgendaConfHallTitleColor }}>
                                                                                                {hall.name}
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="conference-sub-hall">
                                                                                            <div className="sub-hall-title" style={{ color: agendaSettings?.web?.conf_hall_title_color || defaultAgendaConfHallTitleColor }}>
                                                                                                {subHall.name}
                                                                                            </div>
                                                                                            {!!subHall.place && (
                                                                                                <div className="hall-place">
                                                                                                    <i className="bi bi-geo-alt"></i>
                                                                                                    <span>{subHall.place}</span>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                    {/* {renderConferenceHall(subHall)} */}
                                                                                    {subHall.agendas && subHall.agendas.filter(a => (visibleAgendaOnly && !a.hidden) || !visibleAgendaOnly).length ? (
                                                                                        <>
                                                                                            <Timeline className="timeline-wrap" mode="left" titleSpan={5}>
                                                                                                {subHall.agendas
                                                                                                    .filter(a => (visibleAgendaOnly && !a.hidden) || !visibleAgendaOnly)
                                                                                                    .map(agenda => (
                                                                                                        <Timeline.Item label={`${agenda.startTime}～${agenda.endTime}`}>
                                                                                                            <div className="agenda" id={agenda.id} key={agenda.id}>
                                                                                                                <h3 className="subject" style={{ color: agendaSettings?.web?.agenda_task_color || defaultAgendaAgendaTaskColor }}>
                                                                                                                    {agenda.title}
                                                                                                                </h3>
                                                                                                                {!agenda.guestsHidden && !!agenda.guestList && agenda.guestList.length === 1 && (
                                                                                                                    <table>
                                                                                                                        <tr>
                                                                                                                            <td className="guest-name centered">{agenda.guestList[0]?.memUsername?.replace(/^(.)(.)$/, '$1　$2')}</td>
                                                                                                                            <td className="guest-info">
                                                                                                                                <WorkExperiences
                                                                                                                                    workExperiences={agenda.guestList[0]?.ssoMemberWorkExperiencesEntities}
                                                                                                                                    showJobTitle={false}
                                                                                                                                    showSerialNumber={false}
                                                                                                                                    showTooltipStyle={false}
                                                                                                                                    className="guest-work-experience"
                                                                                                                                />
                                                                                                                            </td>
                                                                                                                        </tr>
                                                                                                                    </table>
                                                                                                                )}
                                                                                                                {!agenda.guestsHidden && !!agenda.guestList && agenda.guestList.length > 1 && (
                                                                                                                    <table>
                                                                                                                        {agenda.sortMode > 0 && (
                                                                                                                            <tr className="guest-sort-tip">
                                                                                                                                <td colSpan="2">
                                                                                                                                    <span>{agenda.sortMode === 1 ? '按姓氏拼音排序' : '按姓氏笔画排序'}</span>
                                                                                                                                </td>
                                                                                                                            </tr>
                                                                                                                        )}
                                                                                                                        {agenda.guestList.map((guest, idx) => (
                                                                                                                            <tr key={`agenda_${agenda.id}_expert_${guest.expertId}_${idx}`}>
                                                                                                                                <td className="guest-name centered">{guest?.memUsername?.replace(/^(.)(.)$/, '$1　$2')}</td>
                                                                                                                                <td className="guest-info">
                                                                                                                                    <WorkExperiences workExperiences={guest?.ssoMemberWorkExperiencesEntities} showJobTitle={false} showSerialNumber={false} showTooltipStyle={false} className="guest-work-experience" />
                                                                                                                                </td>
                                                                                                                            </tr>
                                                                                                                        ))}
                                                                                                                    </table>
                                                                                                                )}
                                                                                                                {!agenda.guestList && (
                                                                                                                    <>
                                                                                                                        <td className="guest"></td>
                                                                                                                        <td className="guest-info"></td>
                                                                                                                    </>
                                                                                                                )}
                                                                                                            </div>
                                                                                                        </Timeline.Item>
                                                                                                    ))}
                                                                                            </Timeline>
                                                                                        </>
                                                                                    ) : null}
                                                                                </table>
                                                                            ))
                                                                    ) : (
                                                                        <table key={`hall_${hall.id}`}>
                                                                            <div className="conference-title-wrap">
                                                                                <div className="conference-hall">
                                                                                    <div className="hall-text" style={{ color: agendaSettings?.web?.conf_hall_title_color || defaultAgendaConfHallTitleColor }}>
                                                                                        {hall.name}
                                                                                    </div>
                                                                                    {!!hall.place && (
                                                                                        <div className="hall-place">
                                                                                            <i className="bi bi-geo-alt"></i>
                                                                                            <span>{hall.place}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            {/* {renderConferenceHall(hall)} */}
                                                                            {hall.agendas && hall.agendas.filter(a => (visibleAgendaOnly && !a.hidden) || !visibleAgendaOnly).length ? (
                                                                                <>
                                                                                    <Timeline className="timeline-wrap" mode="left" titleSpan={5}>
                                                                                        {hall.agendas
                                                                                            .filter(a => (visibleAgendaOnly && !a.hidden) || !visibleAgendaOnly)
                                                                                            .map(agenda => (
                                                                                                <Timeline.Item label={`${agenda.startTime}～${agenda.endTime}`}>
                                                                                                    <div className="agenda" key={agenda.id}>
                                                                                                        <h3 className="subject" style={{ color: agendaSettings?.web?.agenda_task_color || defaultAgendaAgendaTaskColor }}>
                                                                                                            {agenda.title}
                                                                                                        </h3>
                                                                                                        {!agenda.guestsHidden && !!agenda.guestList && agenda.guestList.length === 1 && (
                                                                                                            <table>
                                                                                                                <tr>
                                                                                                                    <td className="guest-name centered">{agenda.guestList[0]?.memUsername?.replace(/^(.)(.)$/, '$1　$2')}</td>
                                                                                                                    <td className="guest-info">
                                                                                                                        <WorkExperiences workExperiences={agenda.guestList[0]?.ssoMemberWorkExperiencesEntities} showJobTitle={false} showSerialNumber={false} showTooltipStyle={false} className="guest-work-experience" />
                                                                                                                    </td>
                                                                                                                </tr>
                                                                                                            </table>
                                                                                                        )}
                                                                                                        {!agenda.guestsHidden && !!agenda.guestList && agenda.guestList.length > 1 && (
                                                                                                            <table>
                                                                                                                {agenda.sortMode > 0 && (
                                                                                                                    <tr className="guest-sort-tip">
                                                                                                                        <td colSpan="2">
                                                                                                                            <span>{agenda.sortMode === 1 ? '按姓氏拼音排序' : '按姓氏笔画排序'}</span>
                                                                                                                        </td>
                                                                                                                    </tr>
                                                                                                                )}
                                                                                                                {agenda.guestList.map((guest, idx) => (
                                                                                                                    <tr key={`hall_${hall.id}_agenda_${agenda.id}_expert_${guest.expertId}_${idx}`}>
                                                                                                                        <td className="guest-name centered">{guest?.memUsername?.replace(/^(.)(.)$/, '$1　$2')}</td>
                                                                                                                        <td className="guest-info">
                                                                                                                            <WorkExperiences workExperiences={guest?.ssoMemberWorkExperiencesEntities} showJobTitle={false} showSerialNumber={false} showTooltipStyle={false} className="guest-work-experience" />
                                                                                                                        </td>
                                                                                                                    </tr>
                                                                                                                ))}
                                                                                                            </table>
                                                                                                        )}
                                                                                                        {!agenda.guestList && (
                                                                                                            <>
                                                                                                                <td className="guest-name"></td>
                                                                                                                <td className="guest-info"></td>
                                                                                                            </>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </Timeline.Item>
                                                                                            ))}
                                                                                    </Timeline>
                                                                                </>
                                                                            ) : null}
                                                                        </table>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                            </div>
                                        ))}

                                <div className="code-list">
                                    <div className="qrcode-box">
                                        <div className="qrcode">
                                            <QRCode size={150} value={`${config.HUIYI_DOMAIN}/h5/${conference?.slug}`} />
                                        </div>
                                        <p>长按识别二维码</p>
                                        <p>查看大会官网、全部日程</p>
                                    </div>
                                    <div className="qrcode-box">
                                        <div className="qrcode">
                                            <img src={mpCode} />
                                        </div>
                                        <p>长按识别小程序码</p>
                                        <p>查看个性指南、参与活动</p>
                                    </div>
                                    {!!agendaSettings?.web?.qrcode_img_url && (
                                        <div className="qrcode-box">
                                            <div className="qrcode">
                                                <img src={prepareBCOSSUrl(agendaSettings.web.qrcode_img_url)} />
                                            </div>
                                            {!!agendaSettings?.web?.qrcode_text_line1 && <p>{agendaSettings.web.qrcode_text_line1}</p>}
                                            {!!agendaSettings?.web?.qrcode_text_line2 && <p>{agendaSettings.web.qrcode_text_line2}</p>}
                                        </div>
                                    )}
                                </div>

                                <div className="foot">&copy;{dayjs().format('YYYY')} 健康界 &bull; 医会议</div>
                            </>
                        )}
                    </div>
                    <div className="agenda-preview-foot"></div>
                </div>
            </Drawer>

            {/* H5版日程样式设置Modal */}
            <Modal
                title="H5版日程样式设置"
                width={800}
                open={visibleH5TemplateModal}
                onCancel={closeH5TemplateModal}
                footer={[
                    <Button key="cancel" onClick={closeH5TemplateModal}>
                        取消
                    </Button>,
                    <Button key="save" type="primary" onClick={saveH5TemplateSettings}>
                        保存设置
                    </Button>,
                ]}
            >
                <div>
                    <p>日程预览，按以下样式设置展示日程，并且可以生成分享版日程图片。</p>

                    <Form
                        form={h5TemplateForm}
                        layout="vertical"
                        initialValues={{
                            web: {
                                background_color: '#1677FF',
                                conf_hall_title_color: '#FFFFFF',
                                agenda_task_color: '#1677FF',
                            },
                        }}
                    >
                        <Form.Item name="web">
                            <Form.Item name={['web', 'head_img_url']} label="头图" extra="图片尺寸宽1500px高不小于1000px，支持JPG/PNG格式，大小不超过2MB">
                                {headerImgList.length > 0 && (
                                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: 8 }}>
                                        <Image width={375} src={headerImgList[0]} alt="议程页眉图预览" />
                                        <span
                                            onClick={removeHeaderImg}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                cursor: 'pointer',
                                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                color: '#FFFFFF',
                                                borderRadius: '50%',
                                                width: 20,
                                                height: 20,
                                                textAlign: 'center',
                                                lineHeight: '20px',
                                                fontSize: '12px',
                                            }}
                                        >
                                            <i className="bi bi-x"></i>
                                        </span>
                                    </div>
                                )}

                                <div>
                                    <Upload beforeUpload={file => checkout(file, 5)} fileList={headerImgList} customRequest={imageUpload} maxCount={1}>
                                        <Button icon={<i className="bi bi-upload"></i>}>{headerImgList.length > 0 ? '更改图片' : '上传图片'}</Button>
                                    </Upload>
                                </div>
                            </Form.Item>

                            <Form.Item label="背景色" extra="填写形如 #FFFFFF 的色值，背景色需要和背景图配合（融合），用于背景图高度不够时填补背景">
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                        <Form.Item noStyle shouldUpdate>
                                            {({ getFieldValue }) => {
                                                const color = getFieldValue(['web', 'background_color']);
                                                return (
                                                    !!color && (
                                                        <div
                                                            style={{
                                                                backgroundColor: `${color}`,
                                                                width: 20,
                                                                height: 20,
                                                                border: '1px solid #ccc',
                                                                borderRadius: 4,
                                                                marginRight: 8,
                                                            }}
                                                        ></div>
                                                    )
                                                );
                                            }}
                                        </Form.Item>

                                        <Form.Item noStyle name={['web', 'background_color']} rules={[{ pattern: /^#[0-9a-f]{6}$/i, message: '色值不符合格式要求' }]}>
                                            <Input style={{ width: 120 }} maxLength={7} placeholder="请输入色值" allowClear />
                                        </Form.Item>
                                    </div>
                                    <div>
                                        {colorList.map((item, index) => (
                                            <Tooltip key={index} placement="top" title="点击使用该颜色">
                                                <div
                                                    onClick={() => changeBgColor(item, ['web', 'background_color'])}
                                                    style={{
                                                        backgroundColor: `${item}`,
                                                        width: 20,
                                                        height: 20,
                                                        border: '1px solid #ccc',
                                                        borderRadius: 4,
                                                        display: 'inline-block',
                                                        marginRight: 8,
                                                        marginBottom: 8,
                                                        cursor: 'pointer',
                                                    }}
                                                ></div>
                                            </Tooltip>
                                        ))}
                                    </div>
                                </div>
                            </Form.Item>

                            <Form.Item label="论坛名称文字颜色" extra="填写形如 #FFFFFF 的色值，该颜色用于会议论坛名称的展示">
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                        <Form.Item noStyle shouldUpdate>
                                            {({ getFieldValue }) => {
                                                const color = getFieldValue(['web', 'conf_hall_title_color']);
                                                return (
                                                    !!color && (
                                                        <div
                                                            style={{
                                                                backgroundColor: `${color}`,
                                                                width: 20,
                                                                height: 20,
                                                                border: '1px solid #ccc',
                                                                borderRadius: 4,
                                                                marginRight: 8,
                                                            }}
                                                        ></div>
                                                    )
                                                );
                                            }}
                                        </Form.Item>

                                        <Form.Item noStyle name={['web', 'conf_hall_title_color']} rules={[{ pattern: /^#[0-9a-f]{6}$/i, message: '色值不符合格式要求' }]}>
                                            <Input style={{ width: 120 }} maxLength={7} placeholder="请输入色值" allowClear />
                                        </Form.Item>
                                    </div>
                                    <div>
                                        {colorList.map((item, index) => (
                                            <Tooltip key={index} placement="top" title="点击使用该颜色">
                                                <div
                                                    onClick={() => changeBgColor(item, ['web', 'conf_hall_title_color'])}
                                                    style={{
                                                        backgroundColor: `${item}`,
                                                        width: 20,
                                                        height: 20,
                                                        border: '1px solid #ccc',
                                                        borderRadius: 4,
                                                        display: 'inline-block',
                                                        marginRight: 8,
                                                        marginBottom: 8,
                                                        cursor: 'pointer',
                                                    }}
                                                ></div>
                                            </Tooltip>
                                        ))}
                                    </div>
                                </div>
                            </Form.Item>

                            <Form.Item label="日程任务文字颜色" extra="填写形如 #FFFFFF 的色值，该颜色用于日程任务的展示">
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                        <Form.Item noStyle shouldUpdate>
                                            {({ getFieldValue }) => {
                                                const color = getFieldValue(['web', 'agenda_task_color']);
                                                return (
                                                    !!color && (
                                                        <div
                                                            style={{
                                                                backgroundColor: `${color}`,
                                                                width: 20,
                                                                height: 20,
                                                                border: '1px solid #ccc',
                                                                borderRadius: 4,
                                                                marginRight: 8,
                                                            }}
                                                        ></div>
                                                    )
                                                );
                                            }}
                                        </Form.Item>

                                        <Form.Item noStyle name={['web', 'agenda_task_color']} rules={[{ pattern: /^#[0-9a-f]{6}$/i, message: '色值不符合格式要求' }]}>
                                            <Input style={{ width: 120 }} maxLength={7} placeholder="请输入色值" allowClear />
                                        </Form.Item>
                                    </div>
                                    <div>
                                        {colorList.map((item, index) => (
                                            <Tooltip key={index} placement="top" title="点击使用该颜色">
                                                <div
                                                    onClick={() => changeBgColor(item, ['web', 'agenda_task_color'])}
                                                    style={{
                                                        backgroundColor: `${item}`,
                                                        width: 20,
                                                        height: 20,
                                                        border: '1px solid #ccc',
                                                        borderRadius: 4,
                                                        display: 'inline-block',
                                                        marginRight: 8,
                                                        marginBottom: 8,
                                                        cursor: 'pointer',
                                                    }}
                                                ></div>
                                            </Tooltip>
                                        ))}
                                    </div>
                                </div>
                            </Form.Item>

                            <Form.Item name={['web', 'qrcode_img_url']} label="社群二维码" extra={<span>图片尺寸300×300px，支持JPG/PNG格式，大小不超过1MB</span>}>
                                {qrcodeImg.length > 0 && (
                                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: 8 }}>
                                        <Image width={150} height={150} src={qrcodeImg[0]} alt="议程二维码预览" />
                                        <span
                                            onClick={removeQrcodeImg}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                cursor: 'pointer',
                                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                color: '#FFFFFF',
                                                borderRadius: '50%',
                                                width: 20,
                                                height: 20,
                                                textAlign: 'center',
                                                lineHeight: '20px',
                                                fontSize: '12px',
                                            }}
                                        >
                                            <i className="bi bi-x"></i>
                                        </span>
                                    </div>
                                )}

                                <div>
                                    <Upload beforeUpload={file => checkout(file, 1)} fileList={qrcodeImg} customRequest={qrcodeUpload} maxCount={1}>
                                        <Button icon={<i className="bi bi-upload"></i>}>{qrcodeImg.length > 0 ? '更改图片' : '上传图片'}</Button>
                                    </Upload>
                                </div>
                            </Form.Item>

                            <Form.Item name={['web', 'qrcode_text_line1']}>
                                <Input placeholder="第1行社群二维码文案" maxLength={12} allowClear showCount />
                            </Form.Item>

                            <Form.Item name={['web', 'qrcode_text_line2']}>
                                <Input placeholder="第2行社群二维码文案" maxLength={12} allowClear showCount />
                            </Form.Item>
                        </Form.Item>
                    </Form>
                </div>
            </Modal>
        </>
    );
};

export default PreviewAgendasDrawer;
